import { useMutation } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';

export interface VanishOptions {
  /** List of relay URLs to target. Pass ['ALL_RELAYS'] for a global vanish. */
  relays: string[];
  /** Optional reason / legal notice included in the event content. */
  reason?: string;
}

export interface VanishResult {
  relay: string;
  status: 'sent' | 'error';
  error?: string;
}

/**
 * Publishes a NIP-62 kind:62 "Request to Vanish" event.
 *
 * Per-relay mode: publishes the event directly to each targeted relay via
 * a raw WebSocket connection so the relay tag matches the actual destination.
 *
 * Global mode (relay = 'ALL_RELAYS'): broadcasts to every relay in the
 * provided list so as many relays as possible receive the request.
 */
export function useVanish() {
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async ({ relays, reason }: VanishOptions): Promise<VanishResult[]> => {
      if (!user) throw new Error('User is not logged in');

      const isGlobal = relays.length === 1 && relays[0] === 'ALL_RELAYS';

      // Build and sign the kind 62 event
      const tags: string[][] = relays.map((r) => ['relay', r]);

      const unsigned = {
        kind: 62,
        content: reason?.trim() ?? '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      };

      const signed = await user.signer.signEvent(unsigned);

      const message = JSON.stringify(['EVENT', signed]);

      if (isGlobal) {
        // For a global vanish, the caller passes in all relay URLs they want
        // to broadcast to. We send to each one.
        return broadcastToRelays(relays, message);
      }

      // Per-relay: send the signed event individually to each targeted relay
      return broadcastToRelays(relays, message);
    },
  });
}

/**
 * Opens a WebSocket connection to each relay and sends the pre-serialized
 * EVENT message. Returns an array of per-relay results.
 */
async function broadcastToRelays(relayUrls: string[], message: string): Promise<VanishResult[]> {
  return Promise.all(
    relayUrls.map((relay) =>
      sendToRelay(relay, message)
        .then(() => ({ relay, status: 'sent' as const }))
        .catch((err: unknown) => ({
          relay,
          status: 'error' as const,
          error: err instanceof Error ? err.message : 'Unknown error',
        }))
    )
  );
}

function sendToRelay(relayUrl: string, message: string): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    const timeout = setTimeout(() => {
      ws.close();
      settle(() => reject(new Error('Timeout')));
    }, 8000);

    const ws = new WebSocket(relayUrl);

    ws.onopen = () => {
      ws.send(message);
      // Give relay a moment to accept before closing
      setTimeout(() => {
        ws.close();
        clearTimeout(timeout);
        settle(resolve);
      }, 1500);
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      ws.close();
      settle(() => reject(new Error('WebSocket error')));
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data as string);
        // ["OK", eventId, accepted, message]
        if (Array.isArray(data) && data[0] === 'OK') {
          clearTimeout(timeout);
          ws.close();
          if (data[2] === true) {
            settle(resolve);
          } else {
            settle(() => reject(new Error(data[3] ?? 'Relay rejected event')));
          }
        }
      } catch { /* ignore parse errors */ }
    };
  });
}
